import { z } from 'zod';
import type { SENDING_DOMAIN_STATUSES } from './customer-emails';

/**
 * "Your domain" (MAP 249 spec §7, ticket 08): a shop sends its customer emails as
 * `{asciiName} <office@joesplumbing.com>` through ServWell's Resend account, once it has added
 * four DNS records: Resend's three (DKIM, and the Return-Path's MX and SPF under `servwell.`) and
 * ServWell's own `_servwell` TXT, which binds the domain to THIS shop (D §4c: Resend binds its
 * proof to ServWell's account, not to a shop, so without a per-shop token whoever typed the
 * domain first would send from it once the real owner added the records).
 *
 * Shared: the vocabulary the server decides with and the screen says, never a decision. Whether
 * a domain is sendable is the server's (`services/sending-domain.ts`).
 */

export type ResendDomainStatus = (typeof SENDING_DOMAIN_STATUSES)[number];

/** The Return-Path label Resend's SPF records go under (spec §7: `custom_return_path`). */
export const RETURN_PATH_LABEL = 'servwell';

/** ServWell's own proof record: `_servwell.{domain}` TXT `servwell-verify={shop token}`. */
export const TOKEN_LABEL = '_servwell';
export const TOKEN_PREFIX = 'servwell-verify=';
export const tokenRecordValue = (token: string): string => `${TOKEN_PREFIX}${token}`;

/** "Check again" asks the email service at most this often, per shop (spec §7). */
export const CHECK_AGAIN_SECONDS = 60;
/** A domain still being set up (or one that stopped working) is checked this often. */
export const DOMAIN_POLL_MINUTES = 10;
/** …for this long after it was added, or stopped working (Resend's own window, D §1c). */
export const DOMAIN_PENDING_HOURS = 72;
/** A domain not verified this long after it was added is removed (spec §7). */
export const DOMAIN_CLEANUP_DAYS = 7;
/**
 * Domains one shop may add in a day: each one is an object in ServWell's Resend account, which
 * a plan counts (Pro: 10, +100 for $20; D §2a), so adding and removing cannot be a loop.
 */
export const DOMAIN_ADDS_PER_DAY = 5;

/** The optional DMARC record suggested when the domain publishes none (D rec 8). */
export const DMARC_SUGGESTION = 'v=DMARC1; p=none;';

// ── The owner's input ────────────────────────────────────────────────────────────────────

export const sendingDomainInput = z
  .object({
    /** The domain, as the owner typed it ("JoesPlumbing.com", "https://joesplumbing.com/"). */
    domain: z.string().trim().min(1).max(300),
    /** The part before the @ of the address customers see ("office"). */
    fromLocalPart: z.string().trim().min(1).max(100),
  })
  .strict();
export type SendingDomainInput = z.infer<typeof sendingDomainInput>;

/**
 * A typed domain as ONE spelling: the host of whatever was typed (a scheme, a path, a port, a
 * leading `@` or `www.`-less form are all forgiven), lowercased, with no trailing dot, and an
 * internationalised domain in its ASCII (punycode) form, as DNS and Resend know it. `URL` does
 * the IDNA mapping the same way in the browser and in Node. Empty when nothing usable was typed.
 */
export function normalizeDomain(input: string): string {
  let s = input.trim().toLowerCase();
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/^@+/, '');
  s = s.split(/[/?#\s]/)[0] ?? '';
  s = s.replace(/:\d+$/, '').replace(/\.+$/, '');
  if (!s) return '';
  try {
    return new URL(`http://${s}`).hostname.replace(/\.+$/, '');
  } catch {
    return s;
  }
}

/** The CHECK's rule (0045), and DNS's: letters, digits and inner hyphens, 2+ labels. */
const DOMAIN_SHAPE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
/** A top-level label: letters, or an internationalised one (`xn--`). */
const TLD = /^([a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;
/** 0045's local-part CHECK, and no two dots in a row (a mailbox refuses them). */
const LOCAL_SHAPE = /^[a-z0-9]([a-z0-9._-]{0,62}[a-z0-9])?$/;

/**
 * Mail providers whose domains no shop controls: their DNS is the provider's, so the records can
 * never be added there. A shop that sends from one connects its mailbox instead (spec §8).
 */
export const FREE_MAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'gmx.net',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'comcast.net',
  'att.net',
  'verizon.net',
] as const;

/** Addresses nobody reads: customers' replies go to the From (spec §2, Reply-To = From). */
const NO_REPLY_LOCAL = /^(no-?reply|do-?not-?reply|postmaster|mailer-daemon|bounces?)$/;

/** The sentences a refused "Add domain" reads (and the screen shows as they stand). */
export const DOMAIN_REFUSAL = {
  empty: 'Type your domain, like joesplumbing.com.',
  shape: 'That doesn’t look like a domain. Type it like joesplumbing.com.',
  freeMail: (domain: string) =>
    `${domain} isn’t a domain you can set up: only its provider can change its records. Use your own domain, like joesplumbing.com.`,
  servwell: 'A domain with “ServWell” in it can’t be used.',
  platform: 'That domain is ServWell’s own email domain, so a shop can’t use it.',
  localShape:
    'The part before the @ can use letters, numbers, dots, dashes and underscores, like office.',
  noReply: 'Use an address your customers can reply to, like office: replies go to it.',
  /** The From's local part looks like a brand (MAP 249 final fix-up, /cso B4). */
  localBrand: (brand: string) =>
    `The part before the @ looks like ${brand}. Use your shop’s own name or a plain word, like office.`,
  localServwell: 'The part before the @ can’t contain “ServWell”.',
  alreadyHasOne: (domain: string) =>
    `Your shop already has ${domain} set up. Disconnect it first to use another.`,
  registeredElsewhere: (domain: string) =>
    `This domain is already connected to another email service. Use a subdomain like mail.${domain}, or keep sending from ServWell’s address.`,
  notConfigured:
    'Email isn’t switched on yet, so a domain can’t be set up. Contact ServWell.',
  unreachable: 'We couldn’t reach the email service. Try again in a minute.',
  limit: 'ServWell can’t add another domain right now. Contact ServWell.',
  addsPerDay: `You’ve added ${DOMAIN_ADDS_PER_DAY} domains today. Try again tomorrow.`,
  none: 'Your shop has no domain set up.',
  // "It was", not "You": the 10-minute poll and the nightly check set the same clock, so the
  // owner may never have pressed it (MAP 249 QA D10).
  checkedRecently: (seconds: number) =>
    `It was checked less than a minute ago. Try again in ${seconds} second${seconds === 1 ? '' : 's'}.`,
  notReadyForTest:
    'Your domain isn’t ready to send from yet, so a test can’t go from it.',
  busy: 'Your domain is in the middle of another change. Try again in a minute.',
} as const;

/**
 * What is wrong with a typed domain (already `normalizeDomain`d), as the sentence, or null. The
 * server's rules; ServWell's own domains and the "ServWell" lookalikes are refused there too
 * (they need the server's configuration and its confusables table).
 */
export function domainProblem(domain: string): string | null {
  if (!domain) return DOMAIN_REFUSAL.empty;
  if (domain.length > 253 || !DOMAIN_SHAPE.test(domain)) return DOMAIN_REFUSAL.shape;
  const labels = domain.split('.');
  if (labels.some((l) => l.length > 63)) return DOMAIN_REFUSAL.shape;
  if (!TLD.test(labels[labels.length - 1]!)) return DOMAIN_REFUSAL.shape;
  if ((FREE_MAIL_DOMAINS as readonly string[]).includes(domain)) {
    return DOMAIN_REFUSAL.freeMail(domain);
  }
  return null;
}

/**
 * A reply address that looks like a brand (MAP 249 final fix-up, /cso B4): its customers see it in
 * Reply-To and under every email.
 */
export const REPLY_ADDRESS_BRAND_REFUSAL = {
  brand: (email: string, brand: string) =>
    `${email} looks like ${brand}. Use your shop’s own address: your customers see it on every reply.`,
  servwell: 'A reply address can’t contain “ServWell”: use your shop’s own address.',
} as const;

/** What is wrong with the From address's local part (lowercased), or null. */
export function localPartProblem(local: string): string | null {
  if (!LOCAL_SHAPE.test(local) || local.includes('..')) return DOMAIN_REFUSAL.localShape;
  if (NO_REPLY_LOCAL.test(local)) return DOMAIN_REFUSAL.noReply;
  return null;
}

// ── What Settings → Email shows ──────────────────────────────────────────────────────────

/**
 * Where a shop's domain stands, as the server worked it out (most telling first):
 *  - `held`: its name looks like a brand on ServWell's list; it never sends until staff clear it;
 *  - `ready`: verified, and the shop's token is there: it can send;
 *  - `failing`: it worked, and stopped (records gone, refused by the email service, or taken by
 *    another shop whose token appeared): email goes from ServWell's address meanwhile;
 *  - `in_use_elsewhere`: verified with this shop's token, but another ServWell shop holds it;
 *  - `token_missing`: the email service verified it, the `_servwell` record is missing;
 *  - `not_found`: the email service gave up finding the records (72 hours);
 *  - `checking`: records being looked for.
 */
export const DOMAIN_STATES = [
  'held',
  'ready',
  'failing',
  'in_use_elsewhere',
  'token_missing',
  'not_found',
  'checking',
] as const;
export type DomainState = (typeof DOMAIN_STATES)[number];

/** What each of the four records is for. */
export type DomainRecordPurpose = 'dkim' | 'return_path_mx' | 'return_path_spf' | 'token';

/** One record the shop adds, as a DNS panel asks for it. */
export interface DomainRecordRow {
  purpose: DomainRecordPurpose;
  type: string;
  /** What goes in "Host" / "Name" in the zone's panel (`@` for the zone itself). */
  host: string;
  /** The record's full name. */
  name: string;
  value: string;
  /** An MX record's priority. */
  priority: number | null;
  /** Found yet? Resend's own answer for its three; ours from our lookup. */
  status: 'verified' | 'pending' | 'failed' | 'not_started';
}

/** The DNS provider, worked out from the domain's nameservers, and how to add records there. */
export interface DnsProviderHint {
  id: string;
  name: string;
  hint: string;
}

/** "Your domain" as Settings → Email shows it (`emailDomain.get`). */
export interface SendingDomainView {
  domain: string;
  /** `office@joesplumbing.com`. */
  fromAddress: string;
  /** What customers see in From: `Joes Plumbing <office@joesplumbing.com>`. */
  fromLine: string;
  state: DomainState;
  resendStatus: ResendDomainStatus | null;
  /** The four records, in the order they are best added. */
  records: DomainRecordRow[];
  provider: DnsProviderHint | null;
  /** Does the domain publish a DMARC record (null: the lookup failed or never ran)? */
  dmarc: 'present' | 'missing' | 'unknown';
  /** The optional suggestion, only while `dmarc` is `missing`. */
  dmarcRecord: { host: string; name: string; value: string } | null;
  createdAt: Date;
  verifiedAt: Date | null;
  failingSince: Date | null;
  lastCheckedAt: Date | null;
  /** When "Check again" may ask next (null: now). */
  checkAgainAt: Date | null;
  /** When a domain that never verified is removed (null once it has verified). */
  removeAt: Date | null;
}

/** The short word beside the domain. */
export const DOMAIN_STATE_BADGE: Record<DomainState, string> = {
  held: 'Waiting for ServWell',
  ready: 'Verified',
  failing: 'Stopped working',
  in_use_elsewhere: 'In use by another shop',
  token_missing: 'Almost there',
  not_found: 'Records not found',
  checking: 'Checking',
};

/** The sentence under it. */
export function domainStateLine(
  v: Pick<SendingDomainView, 'state' | 'fromAddress'>,
): string {
  switch (v.state) {
    case 'held':
      return 'ServWell is checking this domain before you can send from it. We’ll be in touch.';
    case 'ready':
      return `Verified: your customer emails can go from ${v.fromAddress}.`;
    case 'failing':
      return 'Your domain stopped working: we’re sending from ServWell’s address until it’s fixed. Check the records below, then press Check again.';
    case 'in_use_elsewhere':
      return 'Another ServWell shop sends from this domain. If it is yours too, contact ServWell to move it here.';
    case 'token_missing':
      return 'Almost there: add the _servwell record, so we know this domain is your shop’s.';
    case 'not_found':
      return 'We couldn’t find the records. Check each one against the table, then press Check again.';
    case 'checking':
      return 'Add these records where your domain’s DNS is managed. We check every 10 minutes: it usually takes about 15 minutes after you add them, and can take up to 72 hours.';
  }
}

/** The address customers see. */
export const fromAddressOf = (localPart: string, domain: string): string =>
  `${localPart}@${domain}`;

/**
 * A record's name as the zone's panel asks for it: relative to the zone (`@` for the zone
 * itself). A name outside the zone is shown whole.
 */
export function hostInZone(name: string, zone: string | null): string {
  if (!zone) return name;
  if (name === zone) return '@';
  return name.endsWith(`.${zone}`) ? name.slice(0, -(zone.length + 1)) : name;
}

/**
 * Resend's record name (relative, as its API answers: `servwell`, `resend._domainkey`) as a full
 * name under `domain`. ⚠️ For a SUBDOMAIN (`mail.joesplumbing.com` in zone `joesplumbing.com`)
 * Resend may answer names relative to the zone (`servwell.mail`) rather than to the domain; a name
 * already ending in the subdomain's own labels is read that way (D open probe 4: confirm live).
 */
export function recordFullName(
  name: string,
  domain: string,
  zone: string | null,
): string {
  const n = name.replace(/\.+$/, '').toLowerCase();
  if (n === domain || n.endsWith(`.${domain}`)) return n;
  if (n === '@' || n === '') return domain;
  if (zone && domain !== zone && domain.endsWith(`.${zone}`)) {
    const sub = domain.slice(0, -(zone.length + 1));
    if (n === sub || n.endsWith(`.${sub}`)) return `${n}.${zone}`;
  }
  return `${n}.${domain}`;
}

/** Every record as plain text, for "Copy all" (to paste into the DNS panel, or send to whoever runs it). */
export function recordsText(
  domain: string,
  records: readonly DomainRecordRow[],
  dmarc?: SendingDomainView['dmarcRecord'],
): string {
  const block = (r: {
    type: string;
    host: string;
    name: string;
    value: string;
    priority?: number | null;
  }) =>
    [
      `Type: ${r.type}`,
      `Host: ${r.host}`,
      `Full name: ${r.name}`,
      `Value: ${r.value}`,
      ...(r.priority !== null && r.priority !== undefined
        ? [`Priority: ${r.priority}`]
        : []),
    ].join('\n');
  const parts = [
    `DNS records for ${domain} (ServWell email)`,
    ...records.map(block),
    ...(dmarc ? [`Optional:\n${block({ type: 'TXT', ...dmarc })}`] : []),
  ];
  return parts.join('\n\n');
}

// ── DNS providers, by nameserver (D rec 8) ───────────────────────────────────────────────

/**
 * Said for every provider: the one mistake every panel invites, shown on one of the shop's OWN
 * records (MAP 249 QA D8: every shop was shown `servwell.joesplumbing.com`). The example is the
 * return path's (`servwell` under the shop's domain, as the old sentence had it), else the first
 * record whose host is not its whole name; with none, the sentence without an example.
 */
export function dnsHostHint(
  records: readonly Pick<DomainRecordRow, 'purpose' | 'host' | 'name'>[],
): string {
  const relative = records.filter((r) => r.host !== '@' && r.host !== r.name);
  const example = relative.find((r) => r.purpose === 'return_path_mx') ?? relative[0];
  const shown = example ? ` (${example.host}, not ${example.name})` : '';
  return `In Host, most providers want only the part before your domain${shown}. A few want the whole name: if a record isn’t found after an hour, try the other way.`;
}

const PROVIDERS: readonly (DnsProviderHint & { match: RegExp })[] = [
  {
    id: 'godaddy',
    name: 'GoDaddy',
    match: /\.domaincontrol\.com$/,
    hint: 'In GoDaddy, open your domain’s DNS, then Add New Record. Host is only the part before your domain. For the MX record, set Priority to 10.',
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    match: /\.ns\.cloudflare\.com$/,
    hint: 'In Cloudflare, open DNS → Records and add each one. Cloudflare adds your domain to the name for you.',
  },
  {
    id: 'namecheap',
    name: 'Namecheap',
    match: /\.registrar-servers\.com$/,
    hint: 'In Namecheap, open Advanced DNS. Set Mail Settings to Custom MX before you add the MX record.',
  },
  {
    id: 'squarespace',
    name: 'Squarespace (Google Domains)',
    match: /\.googledomains\.com$|\.squarespacedns\.com$/,
    hint: 'In Squarespace Domains, open DNS → Custom records and add each one.',
  },
  {
    id: 'wix',
    name: 'Wix',
    match: /\.wixdns\.net$/,
    hint: 'In Wix, open Domains, then Manage DNS records, and add each one in its section.',
  },
  {
    id: 'route53',
    name: 'Amazon Route 53',
    match: /\.awsdns-\d+\.(com|net|org|co\.uk)$/,
    hint: 'In Route 53, create each record in your hosted zone. Put each TXT value in double quotes.',
  },
  {
    id: 'hostinger',
    name: 'Hostinger',
    match: /\.dns-parking\.com$|\.hostinger\.[a-z.]+$/,
    hint: 'In Hostinger, open Domains → DNS / Nameservers and add each record.',
  },
  {
    id: 'ionos',
    name: 'IONOS',
    match: /\.ui-dns\.(com|org|de|biz)$/,
    hint: 'In IONOS, open Domains & SSL, then your domain’s DNS, and add each record.',
  },
  {
    id: 'networksolutions',
    name: 'Network Solutions',
    match: /\.worldnic\.com$/,
    hint: 'In Network Solutions, open your domain’s Advanced DNS records and add each one.',
  },
  {
    id: 'bluehost',
    name: 'Bluehost',
    match: /\.bluehost\.com$/,
    hint: 'In Bluehost, open Domains, then DNS, and add each record.',
  },
  {
    id: 'microsoft',
    name: 'Microsoft 365',
    match: /\.bdm\.microsoftonline\.com$/,
    hint: 'In the Microsoft 365 admin center, open Settings → Domains, your domain, then DNS records.',
  },
  {
    id: 'digitalocean',
    name: 'DigitalOcean',
    match: /\.digitalocean\.com$/,
    hint: 'In DigitalOcean, open Networking → Domains and add each record.',
  },
  {
    id: 'vercel',
    name: 'Vercel',
    match: /\.vercel-dns\.com$/,
    hint: 'In Vercel, open your domain’s DNS Records and add each one.',
  },
];

/** The provider whose nameservers these are, or null. */
export function dnsProviderOf(nameservers: readonly string[]): DnsProviderHint | null {
  for (const ns of nameservers) {
    const host = ns.toLowerCase().replace(/\.+$/, '');
    const p = PROVIDERS.find((x) => x.match.test(host));
    if (p) return { id: p.id, name: p.name, hint: p.hint };
  }
  return null;
}
